import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import config, { isDefaultCluster } from '../Config';
import { authFetch } from '../functions/authFetch';
import { HandleResponse } from '../functions/HandleResponse';
import './pages.css';

const PROTECTED = 'mermadic-env';

// Keys become environment variable names, so case is free but a leading digit
// would make the variable unreachable from most shells.
const ENV_KEY_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
const ENV_KEY_HINT = 'Use letters, digits and underscores, starting with a letter or underscore.';

const emptyRow = () => ({ key: '', value: '' });

export default function ConfigManagement() {
  const navigate = useNavigate();

  // list state
  const [maps, setMaps] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // detail/edit panel
  const [selected, setSelected] = useState(null);   // name string
  const [detailLoading, setDetailLoading] = useState(false);
  const [rows, setRows] = useState([]);              // [{key,value}]
  const [detailError, setDetailError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // delete
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // unsaved-changes guard: name of map user wants to switch to while create is open
  const [discardTarget, setDiscardTarget] = useState(null);

  // create panel
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState('kv'); // 'kv' | 'file'
  const [createName, setCreateName] = useState('');
  const [createRows, setCreateRows] = useState([emptyRow()]);
  const [createFile, setCreateFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createMsg, setCreateMsg] = useState(null);

  const base = `${config.RUNNER_API}/v1/deployment`;

  // ── list ────────────────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await authFetch(`${base}/list-configmap`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const data = await res.json();
        setMaps(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setListError('Failed to load ConfigMaps.');
    } finally {
      setListLoading(false);
    }
  }, [base, navigate]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ── create form has user input? ──────────────────────────────────────────────
  const createHasInput = () =>
    createName.trim() !== '' ||
    createFile !== null ||
    createRows.some(r => r.key.trim() !== '' || r.value.trim() !== '');

  // ── view/select ──────────────────────────────────────────────────────────────
  const doSelectMap = useCallback(async (name) => {
    setShowCreate(false);
    setCreateError(null);
    setCreateMsg(null);
    setSelected(name);
    setDetailLoading(true);
    setDetailError(null);
    setSaveMsg(null);
    setRows([]);
    try {
      const res = await authFetch(`${base}/view-configmap?name=${encodeURIComponent(name)}`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const { data } = await res.json();
        setRows(Object.entries(data || {}).map(([k, v]) => ({ key: k, value: v })));
      }
    } catch (e) {
      setDetailError('Failed to load ConfigMap details.');
    } finally {
      setDetailLoading(false);
    }
  }, [base, navigate]);

  const selectMap = useCallback((name) => {
    if (name === PROTECTED) return;
    if (showCreate && createHasInput()) {
      setDiscardTarget(name);
      return;
    }
    doSelectMap(name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate, createName, createFile, createRows, doSelectMap]);

  // ── edit helpers ─────────────────────────────────────────────────────────────
  const updateRow = (i, field, val) =>
    setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const addRow = () => setRows(r => [...r, emptyRow()]);

  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));

  // ── save (delete + recreate) ─────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    setDetailError(null);
    try {
      const dataObj = {};
      for (const { key, value } of rows) {
        const k = key.trim();
        if (!k) continue;
        if (!ENV_KEY_RE.test(k)) {
          setDetailError(`Key "${k}" is not a valid environment variable name. ${ENV_KEY_HINT}`);
          setSaving(false);
          return;
        }
        dataObj[k] = value;
      }

      // delete first
      await authFetch(`${base}/delete-configmap?name=${encodeURIComponent(selected)}`, { method: 'DELETE' });

      // recreate
      const res = await authFetch(`${base}/create-configmap?name=${encodeURIComponent(selected)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataObj),
      });
      await HandleResponse(res, navigate);
      if (res.ok) {
        setSaveMsg('ConfigMap saved successfully.');
        fetchList();
      } else {
        const err = await res.json().catch(() => ({}));
        setDetailError(err.detail || 'Save failed.');
      }
    } catch (e) {
      setDetailError('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── delete ───────────────────────────────────────────────────────────────────
  const handleDelete = async (name) => {
    setDeleting(true);
    try {
      const res = await authFetch(`${base}/delete-configmap?name=${encodeURIComponent(name)}`, { method: 'DELETE' });
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        setListError(data.detail || `ConfigMap "${name}" is in use by a deployment and cannot be deleted.`);
        return;
      }
      await HandleResponse(res, navigate);
      if (res.ok) {
        if (selected === name) setSelected(null);
        setListError(null);
        fetchList();
      }
    } catch (e) {
      // swallow — list refresh handles visual state
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  // ── create ───────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    setCreateMsg(null);
    const name = createName.trim();
    if (!name) { setCreateError('Name is required.'); setCreating(false); return; }

    try {
      let res;
      if (createMode === 'file') {
        if (!createFile) { setCreateError('Please select a .env file.'); setCreating(false); return; }
        const form = new FormData();
        form.append('env_file', createFile);
        res = await authFetch(`${base}/create-configmap-from-file?name=${encodeURIComponent(name)}`, {
          method: 'POST',
          body: form,
        });
      } else {
        const dataObj = {};
        for (const { key, value } of createRows) {
          const k = key.trim();
          if (!k) continue;
          if (!ENV_KEY_RE.test(k)) {
            setCreateError(`Key "${k}" is not a valid environment variable name. ${ENV_KEY_HINT}`);
            setCreating(false);
            return;
          }
          dataObj[k] = value;
        }
        if (Object.keys(dataObj).length === 0) {
          setCreateError('Add at least one key/value pair.');
          setCreating(false);
          return;
        }
        res = await authFetch(`${base}/create-configmap?name=${encodeURIComponent(name)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataObj),
        });
      }

      await HandleResponse(res, navigate);
      if (res.ok) {
        setCreateMsg(`ConfigMap "${name}" created.`);
        setCreateName('');
        setCreateRows([emptyRow()]);
        setCreateFile(null);
        fetchList();
      } else {
        const err = await res.json().catch(() => ({}));
        setCreateError(err.detail || 'Create failed.');
      }
    } catch (e) {
      setCreateError('Create failed.');
    } finally {
      setCreating(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="cm-page">
      <h2 className="cm-title">
        ConfigMaps
        {isDefaultCluster() && <span className="cj-dedicated-badge">stored as env file on shared</span>}
      </h2>
      <p className="cm-subtitle">
        Manage ConfigMaps used by your deployed applications and jobs.
      </p>

      <div className="cm-layout">
        {/* ── LEFT: list ── */}
        <div className="cm-sidebar">
          <div className="cm-sidebar-header">
            <span>ConfigMaps</span>
            <button
              className="cm-btn cm-btn-primary cm-btn-sm"
              onClick={() => {
                setShowCreate(c => !c);
                setCreateError(null);
                setCreateMsg(null);
                setCreateName('');
                setCreateRows([emptyRow()]);
                setCreateFile(null);
                if (!showCreate) setSelected(null);
              }}
            >
              {showCreate ? 'Cancel' : '+ New'}
            </button>
          </div>

          {listLoading && <p className="cm-hint">Loading…</p>}
          {listError && <p className="cm-error">{listError}</p>}
          {!listLoading && maps.length === 0 && <p className="cm-hint">No ConfigMaps found.</p>}

          <ul className="cm-list">
            {maps.map(name => (
              <li
                key={name}
                className={`cm-list-item ${selected === name ? 'active' : ''} ${name === PROTECTED ? 'protected' : ''}`}
              >
                <span
                  className="cm-list-name"
                  onClick={() => name !== PROTECTED && selectMap(name)}
                  title={name === PROTECTED ? 'Internal — read-only' : name}
                >
                  {name}
                  {name === PROTECTED && <span className="cm-badge">internal</span>}
                </span>
                {name !== PROTECTED && (
                  <button
                    className="cm-btn cm-btn-danger cm-btn-xs"
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(name); }}
                  >
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* ── RIGHT: detail / create ── */}
        <div className="cm-detail">
          {showCreate ? (
            <div className="cm-create-panel">
              <h3 className="cm-panel-title">Create ConfigMap</h3>

              <label className="cm-label">Name</label>
              <div className="cm-input-wrap">
                <input
                  className="cm-input"
                  value={createName}
                  onChange={e => setCreateName(e.target.value)}
                  placeholder="my-app-env"
                />
              </div>

              <div className="cm-mode-tabs">
                <button
                  className={`cm-mode-tab ${createMode === 'kv' ? 'active' : ''}`}
                  onClick={() => setCreateMode('kv')}
                >Key/Value</button>
                <button
                  className={`cm-mode-tab ${createMode === 'file' ? 'active' : ''}`}
                  onClick={() => setCreateMode('file')}
                >.env File</button>
              </div>

              {createMode === 'kv' ? (
                <div className="cm-kv-editor">
                  {createRows.map((row, i) => (
                    <div className="cm-kv-row" key={i}>
                      <input
                        className="cm-input cm-kv-key"
                        placeholder="KEY"
                        value={row.key}
                        onChange={e => setCreateRows(r => r.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))}
                      />
                      <span className="cm-kv-eq">=</span>
                      <input
                        className="cm-input cm-kv-val"
                        placeholder="value"
                        value={row.value}
                        onChange={e => setCreateRows(r => r.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))}
                      />
                      {createRows.length > 1 && (
                        <button className="cm-btn cm-btn-ghost cm-btn-xs" onClick={() => setCreateRows(r => r.filter((_, idx) => idx !== i))}>✕</button>
                      )}
                    </div>
                  ))}
                  <div className="cm-kv-footer">
                    <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => setCreateRows(r => [...r, emptyRow()])}>+ Add row</button>
                    {createError && <p className="cm-error">{createError}</p>}
                    {createMsg && <p className="cm-success">{createMsg}</p>}
                    <button className="cm-btn cm-btn-primary cm-btn-sm" onClick={handleCreate} disabled={creating}>
                      {creating ? 'Creating…' : 'Create ConfigMap'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="cm-file-upload">
                  <div className="mcr-file-upload">
                    <input
                      id="cm-env-file-input"
                      type="file"
                      accept=".env,text/plain"
                      className="mcr-file-input-hidden"
                      onChange={e => setCreateFile(e.target.files[0] || null)}
                    />
                    <button
                      type="button"
                      className="mcr-select-file-btn"
                      onClick={() => document.getElementById('cm-env-file-input')?.click()}
                    >
                      Choose file
                    </button>
                    {createFile && <span className="mcr-file-name">{createFile.name}</span>}
                  </div>
                  <p className="cm-hint">Upload a <code>.env</code> file with <code>KEY=value</code> lines.</p>
                  {createError && <p className="cm-error">{createError}</p>}
                  {createMsg && <p className="cm-success">{createMsg}</p>}
                  <button className="cm-btn cm-btn-primary cm-btn-sm" onClick={handleCreate} disabled={creating}>
                    {creating ? 'Creating…' : 'Create ConfigMap'}
                  </button>
                </div>
              )}
            </div>
          ) : selected ? (
            <div className="cm-edit-panel">
              <div className="cm-edit-header">
                <h3 className="cm-panel-title">{selected}</h3>
                <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => setSelected(null)}>✕ Close</button>
              </div>

              {detailLoading && <p className="cm-hint">Loading…</p>}
              {detailError && <p className="cm-error">{detailError}</p>}

              {!detailLoading && (
                <>
                  <div className="cm-kv-editor">
                    <div className="cm-kv-row cm-kv-header">
                      <span className="cm-kv-key">Key</span>
                      <span className="cm-kv-eq" />
                      <span className="cm-kv-val">Value</span>
                    </div>
                    {rows.map((row, i) => (
                      <div className="cm-kv-row" key={i}>
                        <input
                          className="cm-input cm-kv-key"
                          value={row.key}
                          onChange={e => updateRow(i, 'key', e.target.value)}
                          placeholder="KEY"
                        />
                        <span className="cm-kv-eq">=</span>
                        <input
                          className="cm-input cm-kv-val"
                          value={row.value}
                          onChange={e => updateRow(i, 'value', e.target.value)}
                          placeholder="value"
                        />
                        <button className="cm-btn cm-btn-ghost cm-btn-xs" onClick={() => removeRow(i)}>✕</button>
                      </div>
                    ))}
                    <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={addRow}>+ Add row</button>
                  </div>

                  {saveMsg && <p className="cm-success">{saveMsg}</p>}

                  <div className="cm-edit-actions">
                    <button className="cm-btn cm-btn-primary" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="cm-empty-state">
              <p>Select a ConfigMap from the list to view or edit it,<br />or click <strong>+ New</strong> to create one.</p>
            </div>
          )}

        </div>

        {/* ── GUIDE: instruction panel ── */}
        <div className="page-inst-panel">
          <h4>What is a ConfigMap?</h4>
          <p>
            A ConfigMap stores environment variables that are injected into your app's
            container at runtime — no rebuilding the image needed.
          </p>
          <hr className="inst-divider" />
          <h4>Usage</h4>
          <div className="inst-field">
            <span className="inst-field-name">Keys</span>
            Letters, digits and underscores, starting with a letter or underscore. Case is
            preserved. Accessed in your app via <code>os.environ</code>, <code>process.env</code>, etc.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">Values</span>
            Plain strings — connection strings, feature flags, external URLs.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">.env upload</span>
            Upload a <code>KEY=value</code> file to bulk-create entries.
          </div>
          <hr className="inst-divider" />
          {isDefaultCluster()
            ? <p>Changes take effect on the next job or cron run.</p>
            : <p>Changes take effect on the next <strong>Redeploy</strong> from <Link className="inst-link" to="/deployment/apps">Deployments</Link>.</p>
          }
          <p>
            {isDefaultCluster()
              ? 'On the shared cluster, this is stored as an env file and injected into each job at run time.'
              : 'Stored as a Kubernetes ConfigMap and injected at run time.'}
          </p>
          <p>Treat values as configuration — anyone with access to this ConfigMap can view them.</p>
        </div>
      </div>

      {/* ── unsaved changes modal ── */}
      {discardTarget && (
        <div className="popup" onClick={() => setDiscardTarget(null)}>
          <div className="popup-inner" onClick={e => e.stopPropagation()}>
            <h2>Unsaved Changes</h2>
            <div>You have unsaved changes in the create form. Discard them and open <strong>{discardTarget}</strong>?</div>
            <div className="cm-popup-actions">
              <button className="cm-btn cm-btn-ghost" onClick={() => setDiscardTarget(null)}>Continue Editing</button>
              <button
                className="cm-btn cm-btn-danger"
                onClick={() => {
                  const target = discardTarget;
                  setDiscardTarget(null);
                  setCreateName('');
                  setCreateRows([emptyRow()]);
                  setCreateFile(null);
                  doSelectMap(target);
                }}
              >
                Discard &amp; Open
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── delete confirm modal ── */}
      {deleteConfirm && (
        <div className="cm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="cm-modal" onClick={e => e.stopPropagation()}>
            <h4 className="cm-modal-title">Delete ConfigMap</h4>
            <p className="cm-modal-body">
              Are you sure you want to delete <strong>{deleteConfirm}</strong>? This cannot be undone.
            </p>
            <div className="cm-modal-buttons">
              <button className="cm-btn cm-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="dm-btn-delete" onClick={() => handleDelete(deleteConfirm)} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
